import { call, put, takeEvery, select } from 'redux-saga/effects';
import { constants as ownerDetailsConstants } from '../owner-details/redux';
import { constants } from './redux';
import { actions } from './redux';
import Api from '../../utils/Api';
import {getLocation} from "connected-react-router";
import {convertToTableData, resolveMatrixContext, matrixFilter, scaleFilter} from "./utils";
import { addSuccess } from '../notifications/actions';


// Every refusal the Configure dialog can provoke — dropping a column that
// still carries figures, archiving a component somebody pays, removing a rank
// that is manned or has figures — arrives as a sentence written for the
// person reading it. Anything else falls back to the generic message so the
// dialog never shows "[object Object]".
const refusalMessage = (e) => (
  e?.apiErrors?.[0]?.detail
  || e?.apiErrors?.[0]?.title
  || 'Could not apply the change. Please try again.'
);

// CREW-1299 — the same tab runs on the owner page and on the vessel page.
// Every saga below starts here instead of matching `/owners/:id/wage` itself;
// null means "not on a Wage tab", or a vessel whose matrix is not known yet.
// See `resolveMatrixContext` for what each page shows.
function* matrixContext() {
  const { pathname } = yield select(getLocation);
  const vesselMatrix = yield select(s => s.ownerDetailsWage.vesselMatrix);

  return resolveMatrixContext(pathname, vesselMatrix);
}

function* fetchTypes() {
  try {
    const context = yield call(matrixContext);
    if (!context) return;

    const data = yield call(Api.crewWages.getTypes);

    yield put(actions.loadWageScaleTypesSuccess(data));
  } catch (e) {
    console.error(e);

    yield put(actions.loadWageScaleTypesError(e));
  }
}

// The vessel's own matrix keeps its own alternative nationalities; a vessel
// paid from the owner's matrix shows his.
function* fetchNationalities() {
  try {
    const context = yield call(matrixContext);
    if (!context) return;

    const data = context.vesselId
      ? yield call(Api.crewWages.getVesselNationalities, context.vesselId)
      : yield call(Api.owner.getScaleNationalities, context.ownerId);

    yield put(actions.loadWageNationalitiesSuccess(data));
  } catch (e) {
    console.error(e);

    yield put(actions.loadWageNationalitiesError(e));
  }
}


function* loadWageScales() {
  const context = yield call(matrixContext);
  if (!context) return;

  const {wageType, wageNationality} = yield select(s => s.ownerDetailsWage);
  const filter = scaleFilter(context, wageType, wageNationality);
  const data = yield call(Api.crewWages.getScales, {query: { filter }});
  yield put(actions.loadWageScaleSuccess(data));
}


function* fetchWageScales() {
  try {
    const context = yield call(matrixContext);
    if (!context) return;

    yield put(actions.loadWageScaleRequest());

    yield loadWageScales();
  } catch (e) {
    console.error(e);

    yield put(actions.loadWageScaleError(e));
  }
}

// CREW-1101 — every cell of the matrix is now written by component CODE, the
// seven built-in lines included. `component_amounts` merges rather than
// replaces, so one cell at a time is exactly what it expects, and the resource
// routes a built-in code back into its own column on `crew_wage_scales`. The
// flat `basic`/`overtime`/… attributes still exist and still read the same —
// nothing else on the wire changed.
function* updateWageScale({payload: { id, field, value, cb }}) {
  try {

    const attributes = { component_amounts: { [field]: value } } ;
    yield call(Api.crewWages.updateScale, id, attributes);

    yield loadWageScales();

    if (cb) yield call(cb);
  } catch (e) {
    console.error(e);

    yield put(actions.loadWageScaleError(e));
  }
}

function* loadWageBonuses() {
  const context = yield call(matrixContext);
  if (!context) return;

  const filter = matrixFilter(context);
  const data = yield call(Api.crewWages.getBonuses, {query: { filter }});

  yield put(actions.loadWageBonusesSuccess(convertToTableData(data)));
}

function* fetchWageBonuses() {
  try {
    const context = yield call(matrixContext);
    if (!context) return;

    yield put(actions.loadWageBonusesRequest());

    yield loadWageBonuses();
  } catch (e) {
    console.error(e);

    yield put(actions.loadWageBonusesError(e));
  }
}

function* updateWageBonus({payload: { id, field, value, cb }}) {
  try {

    const attributes = { [field]: value } ;
    yield call(Api.crewWages.updateBonus, id, attributes);

    yield loadWageBonuses();

    if (cb) yield call(cb);
  } catch (e) {
    console.error(e);

    yield put(actions.loadWageScaleError(e));
  }
}

function* createNationalityScale({payload: { nationality_id, cb }}) {
  try {

    const attributes = { nationality_id } ;
    const context = yield call(matrixContext);
    if (!context?.editable) return;

    if (context.vesselId) {
      yield call(Api.crewWages.addVesselNationality, context.vesselId, nationality_id);
    } else {
      yield call(Api.owner.createNationalityScale, context.ownerId, attributes);
    }

    yield put(actions.createNationalityScaleSuccess());
    yield fetchNationalities();
    yield put(actions.wageNationalityChange(nationality_id));
    if (cb) yield call(cb);
  } catch (e) {
    console.error(e);

    yield put(actions.createNationalityScaleError(e));
  }
}



// The columns are the OWNER's set on both pages: a vessel's own matrix pays
// different figures, not different lines, so its cards read his set too.
function* loadWageComponents() {
  const context = yield call(matrixContext);
  if (!context) return;

  const { data } = yield call(Api.wageComponents.getOwnerSet, context.ownerId);

  yield put(actions.loadWageComponentsSuccess({
    components: data?.data || [],
    configured: !!data?.configured,
  }));
}

function* fetchWageComponents() {
  try {
    const context = yield call(matrixContext);
    if (!context) return;

    yield put(actions.loadWageComponentsRequest());

    yield loadWageComponents();
  } catch (e) {
    console.error(e);

    yield put(actions.loadWageComponentsError(e));
  }
}

function* fetchComponentCatalog() {
  try {
    const context = yield call(matrixContext);
    if (context?.page !== 'owner') return;

    yield put(actions.loadComponentCatalogRequest());

    const { data } = yield call(Api.wageComponents.getCatalog);

    yield put(actions.loadComponentCatalogSuccess(
      (data?.data || []).map(({ id, attributes }) => ({ id, ...attributes })),
    ));
  } catch (e) {
    console.error(e);

    yield put(actions.loadComponentCatalogError(e));
  }
}

// Adding or removing a column changes what `wages` and `total` are made of, so
// the matrix has to be re-read, not just the column list.
function* refreshAfterComponentChange() {
  yield loadWageComponents();
  yield fetchWageScales();
}

// The column set is the owner's, so it is only ever changed from his page —
// the vessel page's Configure dialog has no Fields tab.
function* ownerPageContext() {
  const context = yield call(matrixContext);

  return context?.page === 'owner' ? context : null;
}

function* addOwnerComponent({payload: { componentId, cb }}) {
  try {
    const context = yield call(ownerPageContext);
    if (!context) return;

    yield put(actions.clearComponentError());
    yield call(Api.wageComponents.addToOwner, context.ownerId, componentId);
    yield refreshAfterComponentChange();

    if (cb) yield call(cb);
  } catch (e) {
    console.error(e);

    yield put(actions.componentError(refusalMessage(e)));
  }
}

function* removeOwnerComponent({payload: { componentId, cb }}) {
  try {
    const context = yield call(ownerPageContext);
    if (!context) return;

    yield put(actions.clearComponentError());
    yield call(Api.wageComponents.removeFromOwner, context.ownerId, componentId);
    yield refreshAfterComponentChange();

    if (cb) yield call(cb);
  } catch (e) {
    console.error(e);

    yield put(actions.componentError(refusalMessage(e)));
  }
}

function* setComponentPrintCell({payload: { componentId, printCell, cb }}) {
  try {
    const context = yield call(ownerPageContext);
    if (!context) return;

    yield put(actions.clearComponentError());
    yield call(Api.wageComponents.setPrintCell, context.ownerId, componentId, printCell);
    // Only the column list changed — the figures did not, so the matrix is not
    // re-read here.
    yield loadWageComponents();

    if (cb) yield call(cb);
  } catch (e) {
    console.error(e);

    yield put(actions.componentError(refusalMessage(e)));
  }
}

// A brand-new component goes into the catalogue AND straight into this owner's
// set — nobody opens the dialog to create a line they do not intend to pay.
function* createWageComponent({payload: { attributes, cb }}) {
  try {
    yield put(actions.clearComponentError());

    const { data } = yield call(Api.wageComponents.createComponent, attributes);
    const created = data?.data;

    yield fetchComponentCatalog();
    if (created?.id) yield addOwnerComponent({ payload: { componentId: created.id } });

    if (cb) yield call(cb);
  } catch (e) {
    console.error(e);

    yield put(actions.componentError(refusalMessage(e)));
  }
}

// The matrix's rows. Two lists, because they answer different questions: the
// owner's ranks come with the reasons each one may not be dropped, and the
// catalogue is every rank there is — the same list the PPE matrix ticks out
// of, so the two dialogs offer the same names in the same order.
//
// A vessel paid from the owner's matrix has no rows of its own to configure:
// the dialog is not offered there, and nothing is fetched.
function* loadMatrixRanks() {
  const context = yield call(matrixContext);
  if (!context?.editable) return;

  const { data } = yield call(Api.wageComponents.getMatrixRanks, context.ownerId, context.vesselId);

  yield put(actions.loadMatrixRanksSuccess(data?.data || []));
}

function* fetchMatrixRanks() {
  try {
    const context = yield call(matrixContext);
    if (!context?.editable) return;

    yield put(actions.loadMatrixRanksRequest());

    yield loadMatrixRanks();
  } catch (e) {
    console.error(e);

    yield put(actions.rankError(refusalMessage(e)));
  }
}

function* fetchAllRanks() {
  try {
    const context = yield call(matrixContext);
    if (!context?.editable) return;

    // `Api.ranks.index` already sorts by `crewing_priority` — the matrix's
    // own row order, so the grid reads top-down the way the table does.
    const { data: allRanks } = yield call(Api.ranks.index);

    yield put(actions.loadAllRanksSuccess(allRanks));
  } catch (e) {
    console.error(e);

    yield put(actions.rankError(refusalMessage(e)));
  }
}

// A rank carries a whole row of the matrix and a block of the bonuses table,
// so both have to be re-read — the column list does not change here.
function* applyMatrixRanks({ payload: { roleIds, cb } }) {
  try {
    const context = yield call(matrixContext);
    if (!context?.editable) return;

    yield put(actions.clearRankError());
    yield put(actions.loadMatrixRanksRequest());
    yield call(Api.wageComponents.applyMatrixRanks, context.ownerId, roleIds, context.vesselId);

    yield loadMatrixRanks();
    yield fetchWageScales();
    yield fetchWageBonuses();

    if (cb) yield call(cb);
  } catch (e) {
    console.error(e);

    yield put(actions.rankError(refusalMessage(e)));
  }
}

// CREW-1299 — the vessel page. Its Wage tab asks first whose matrix the
// vessel is paid from; VESSEL_MATRIX_LOADED then reads every table on the tab,
// as the owner page's ADD_LOADED_ITEM does there.
function* loadVesselMatrix({ payload: vesselId }) {
  try {
    const { data } = yield call(Api.crewWages.getVesselMatrix, vesselId);

    yield put(actions.vesselMatrixLoaded(data?.data));
  } catch (e) {
    console.error(e);

    yield put(actions.vesselMatrixError(refusalMessage(e)));
  }
}

function* overrideVesselMatrix({ payload: { vesselId, cb } }) {
  try {
    const { data } = yield call(Api.crewWages.overrideVesselMatrix, vesselId);

    yield put(actions.vesselMatrixLoaded(data?.data));
    yield put(addSuccess("The vessel now has its own wage scale, copied from the owner's. Change the figures that differ."));

    if (cb) yield call(cb);
  } catch (e) {
    console.error(e);

    yield put(actions.vesselMatrixError(refusalMessage(e)));
  }
}

function* resetVesselMatrix({ payload: { vesselId, cb } }) {
  try {
    const { data } = yield call(Api.crewWages.resetVesselMatrix, vesselId);

    yield put(actions.vesselMatrixLoaded(data?.data));
    yield put(addSuccess("The vessel is paid from its owner's wage scale again."));

    if (cb) yield call(cb);
  } catch (e) {
    console.error(e);

    yield put(actions.vesselMatrixError(refusalMessage(e)));
  }
}

// Whatever tells the tab that its matrix is known: the owner on his page, the
// vessel's status on the vessel page.
const MATRIX_READY = [ownerDetailsConstants.ADD_LOADED_ITEM, constants.VESSEL_MATRIX_LOADED];

export default function getFeatureSagas() {
  return [
    takeEvery(MATRIX_READY, fetchTypes),
    takeEvery(MATRIX_READY, fetchWageBonuses),
    takeEvery(MATRIX_READY, fetchNationalities),
    takeEvery(
      [...MATRIX_READY, constants.WAGE_TYPE_CHANGE, constants.WAGE_NATIONALITY_CHANGE],
      fetchWageScales
    ),
    takeEvery(constants.UPDATE_WAGE_SCALE_REQUEST, updateWageScale),
    takeEvery(constants.UPDATE_WAGE_BONUSES_REQUEST, updateWageBonus),
    takeEvery(constants.CREATE_NATIONALITY_SCALE_REQUEST, createNationalityScale),
    takeEvery(MATRIX_READY, fetchWageComponents),
    takeEvery(MATRIX_READY, fetchComponentCatalog),
    takeEvery(constants.ADD_OWNER_COMPONENT_REQUEST, addOwnerComponent),
    takeEvery(constants.REMOVE_OWNER_COMPONENT_REQUEST, removeOwnerComponent),
    takeEvery(constants.CREATE_WAGE_COMPONENT_REQUEST, createWageComponent),
    takeEvery(constants.SET_COMPONENT_PRINT_CELL_REQUEST, setComponentPrintCell),
    takeEvery(MATRIX_READY, fetchMatrixRanks),
    takeEvery(MATRIX_READY, fetchAllRanks),
    takeEvery(constants.APPLY_MATRIX_RANKS_REQUEST, applyMatrixRanks),
    takeEvery(constants.LOAD_VESSEL_MATRIX_REQUEST, loadVesselMatrix),
    takeEvery(constants.OVERRIDE_VESSEL_MATRIX_REQUEST, overrideVesselMatrix),
    takeEvery(constants.RESET_VESSEL_MATRIX_REQUEST, resetVesselMatrix),
  ];
}
